import React, { useMemo, useState } from "react";
import { useAppDispatch } from "store";
import { Form, DateRangePicker, Input, HStack } from "rsuite";
import { TagFilter } from "@rsuite/icons";
import { formatDateToLocalISOString } from "utils";
import styled from "styled-components";
import debounce from "lodash/debounce";

import FormControl from "components/base/FormControl";
import FormGroup from "components/base/FormGroup";
import Panel from "components/base/Panel";
import PrimaryButton from "components/Button/PrimaryButton";
import Icon from "components/base/Icon";

import { validationHasErrors } from "utils";
import { FileFilters, filesFormModel } from "types/filterConfig";
import { parseUTC, displayLocalDateToBackendUTCString, toBackendUTCString } from "utils/time";

type FormModelType = typeof filesFormModel;

type FilesFiltersProps = {
    expanded?: boolean;
    model: FormModelType;
    loading: boolean;
    filters: Record<string, any>;
    formError: Record<string, string>;
    notUpdatableKeys: string[];
    currentParamsRef: React.RefObject<URLSearchParams>;
    setFilters: React.Dispatch<React.SetStateAction<FileFilters>>;
    setFormError: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    setRefreshKey: (key: number) => void;
    setSearchParams: (params: URLSearchParams) => void;
};


const FilesFilters: React.FC<FilesFiltersProps> = ({
    expanded,
    model,
    loading,
    filters,
    formError,
    notUpdatableKeys,
    currentParamsRef,
    setFilters,
    setFormError,
    setRefreshKey,
    setSearchParams
}) => {
    const [filtersState, setFiltersState] = useState(filters);

    const [dateTimePickerOpen, setDateTimePickerOpen] = useState(false);
    const [timeRange, setTimeRange] = useState<{
        time_from: Date | null;
        time_to: Date | null;
    }>({
        time_from: parseUTC(filters.time_from),
        time_to: parseUTC(filters.time_to)
    });

    const filtersFormValue = useMemo(() => {
        return {
            time_range: timeRange.time_from && timeRange.time_to
                ? [timeRange.time_from, timeRange.time_to]
                : [],
            source: filters.source || ""
        };
    }, [filters]);

    const handleFilterChange = () => {
        const filterValue: FileFilters = {
            source: filtersState.source || "",
            time_from: timeRange.time_from ? displayLocalDateToBackendUTCString(timeRange.time_from) : "",
            time_to: timeRange.time_to ? displayLocalDateToBackendUTCString(timeRange.time_to) : ""
        };

        const validation = model.check(filterValue);
        const newErrors: Record<string, string> = {};

        Object.entries(validation).forEach(([key, val]: any) => {
            if (val.hasError && !notUpdatableKeys.includes(key)) {
                newErrors[key] = val.errorMessage;
            }
        });

        setFormError(newErrors);

        if (validationHasErrors(validation)) {
            return;
        }


        setFilters(filterValue);

        const updatedParams = new URLSearchParams(currentParamsRef.current.toString());

        Object.entries(filterValue).forEach(([key, value]) => {
            if (value !== undefined && value !== null && !notUpdatableKeys.includes(key)) {
                updatedParams.set(key, value);
            }
        });

        currentParamsRef.current = updatedParams;
        setSearchParams(updatedParams);

        setRefreshKey(Date.now());
    };

    return (
        // <FiltersContainer>
        <Panel
            // header="Filters"
            expanded={expanded}
            noStyles
        >
            <Form
                model={model}
                formError={formError}
                formDefaultValue={filtersFormValue}
                onChange={debounce((value: any) => {
                    const [timeFrom, timeTo] = (value?.time_range as [Date, Date]) ?? [null, null];

                    setTimeRange({ time_from: timeFrom ?? null, time_to: timeTo ?? null });

                    setFiltersState(prev => ({
                        ...prev,
                        source: value?.source || "",
                        time_from: timeFrom ? displayLocalDateToBackendUTCString(timeFrom) : "",
                        time_to: timeTo ? displayLocalDateToBackendUTCString(timeTo) : ""
                    }));
                }, 500)}
            >
                <HStack spacing={16} justifyContent="space-between" alignItems="center">
                    <FormGroup gap={8} horizontal>
                        <FormControl
                            open={dateTimePickerOpen}
                            onOpen={() => setDateTimePickerOpen(true)}
                            onClose={() => setDateTimePickerOpen(false)}
                            // onEnter={() => {}}
                            disabled={loading}
                            editable
                            as={DateRangePicker}
                            width={330}
                            value={
                                timeRange.time_from && timeRange.time_to
                                    ? [timeRange.time_from, timeRange.time_to]
                                    : null
                            }
                            onChange={(val: [Date, Date] | null) => {
                                const timeFrom = val?.[0] ?? null;
                                const timeTo = val?.[1] ?? null;

                                setTimeRange({ time_from: timeFrom, time_to: timeTo });

                                setFiltersState(prev => ({
                                    ...prev,
                                    time_from: timeFrom ? formatDateToLocalISOString(timeFrom) : "",
                                    time_to: timeTo ? formatDateToLocalISOString(timeTo) : ""
                                }));
                            }}
                            onClean={() => {
                                setTimeRange({ time_from: null, time_to: null });
                            }}
                            showOneCalendar
                            size="md"
                            name="time_range"
                            placeholder="Select range"
                            format="dd/MM/yyyy HH:mm:ss"
                            cleanable={false}
                        />
                        <FormControl
                            disabled={loading}
                            width={180}
                            as={Input}
                            placeholder="Source..."
                            name="source"
                            size="md"
                        />
                    </FormGroup>
                    <PrimaryButton
                        disabled={loading}
                        onClick={() => {
                            handleFilterChange();
                        }}
                    >
                        <Icon as={TagFilter} />
                        <span>Show</span>
                    </PrimaryButton>
                </HStack>
            </Form>
        </Panel>
    );
};

export default FilesFilters; 